import { useEffect, useMemo, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { AlertCircle, FileUp, Loader2, Plus, RefreshCw, CheckCircle2, Lock } from "lucide-react";
import { toast } from "sonner";
import { crmApi } from "@/lib/ownerClient";

function StatusPill({ status }) {
  const map = {
    active: "border-emerald-500/40 text-emerald-300",
    draft: "border-amber-500/40 text-amber-300",
    archived: "border-zinc-700 text-zinc-500",
  };
  return (
    <Badge variant="outline" className={map[status] || "border-zinc-700 text-zinc-400"}>
      {status}
    </Badge>
  );
}

function NewBinderDialog({ categories, visibilities, onCreated, isOwner }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    slug: "",
    title: "",
    category: "subscriptions",
    visibility: "sales_facing",
    description: "",
  });
  const submit = async () => {
    if (!form.slug || !form.title) {
      toast.error("Slug and title are required");
      return;
    }
    setBusy(true);
    try {
      const created = await crmApi.createBinder(form);
      toast.success(`Binder “${created.title}” created`);
      setOpen(false);
      setForm({ slug: "", title: "", category: "subscriptions", visibility: "sales_facing", description: "" });
      onCreated?.();
    } catch (exc) {
      toast.error(exc.message || "Failed to create binder");
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" className="bg-amber-500 text-zinc-950 hover:bg-amber-400" data-testid="new-binder-btn">
          <Plus className="h-4 w-4 mr-1" /> New Binder
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-black border-zinc-800 text-white">
        <DialogHeader>
          <DialogTitle>New Sales Knowledge Binder</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Slug (unique, lowercase, hyphens)</Label>
            <Input value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value.replace(/[^a-z0-9-]/gi, "-").toLowerCase() })} data-testid="binder-slug" placeholder="subscriptions-2026" />
          </div>
          <div>
            <Label>Title</Label>
            <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="binder-title" placeholder="Subscriptions — 2026 Edition" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Category</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger data-testid="binder-category"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Visibility</Label>
              <Select value={form.visibility} onValueChange={(v) => setForm({ ...form, visibility: v })} disabled={!isOwner}>
                <SelectTrigger data-testid="binder-visibility"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {visibilities.map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label>Description (optional)</Label>
            <Textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="binder-description" />
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setOpen(false)} disabled={busy}>Cancel</Button>
            <Button onClick={submit} disabled={busy} className="bg-amber-500 text-zinc-950 hover:bg-amber-400" data-testid="binder-create-submit">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function VersionUploader({ binder, onUploaded }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [versionLabel, setVersionLabel] = useState("");
  const [notes, setNotes] = useState("");
  const [file, setFile] = useState(null);

  const submit = async () => {
    if (!file || !versionLabel) {
      toast.error("Pick a file and enter a version label");
      return;
    }
    setBusy(true);
    const fd = new FormData();
    fd.append("version_label", versionLabel);
    fd.append("notes", notes);
    fd.append("file", file);
    try {
      const res = await crmApi.uploadVersion(binder.id, fd);
      toast.success(`Uploaded — ${res.chunk_count} chunks, ${res.fact_count} facts`);
      setOpen(false);
      setVersionLabel("");
      setNotes("");
      setFile(null);
      onUploaded?.();
    } catch (exc) {
      toast.error(exc.message || "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" className="border-zinc-700 text-white hover:bg-zinc-800" data-testid={`upload-version-${binder.slug}`}>
          <FileUp className="h-4 w-4 mr-1" /> Upload version
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-black border-zinc-800 text-white">
        <DialogHeader>
          <DialogTitle>Upload a new version of “{binder.title}”</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Version label</Label>
            <Input value={versionLabel} onChange={(e) => setVersionLabel(e.target.value)} placeholder="v2026.06.15" data-testid="version-label" />
          </div>
          <div>
            <Label>File (PDF, DOCX, PPTX, XLSX, CSV, MD, TXT — up to 10 MB)</Label>
            <Input type="file" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="version-file" />
          </div>
          <div>
            <Label>Notes (optional)</Label>
            <Textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="version-notes" />
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setOpen(false)} disabled={busy}>Cancel</Button>
            <Button onClick={submit} disabled={busy} className="bg-amber-500 text-zinc-950 hover:bg-amber-400" data-testid="version-upload-submit">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Upload + parse + embed"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function BinderRow({ binder, onChange }) {
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(false);

  const reload = async () => {
    setLoading(true);
    try {
      const v = await crmApi.listVersions(binder.id);
      setVersions(v || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const v = await crmApi.listVersions(binder.id);
        if (active) setVersions(v || []);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [binder.id]);

  const activate = async (versionId) => {
    try {
      await crmApi.activateVersion(binder.id, versionId);
      toast.success("Version activated");
      reload();
      onChange?.();
    } catch (exc) {
      toast.error(exc.message || "Failed to activate");
    }
  };

  return (
    <Card className="bg-zinc-900/40 border-zinc-800" data-testid={`binder-row-${binder.slug}`}>
      <CardHeader className="flex flex-row items-start justify-between gap-3 pb-2">
        <div>
          <CardTitle className="text-base text-white flex items-center gap-2">
            {binder.title}
            {binder.visibility === "owner_vault" && <Lock className="h-3.5 w-3.5 text-amber-300" />}
          </CardTitle>
          <div className="text-xs text-zinc-500 mt-0.5">
            <span className="uppercase tracking-wider">{binder.category}</span> · {binder.slug}
          </div>
          {binder.description && <p className="text-xs text-zinc-400 mt-2">{binder.description}</p>}
        </div>
        <div className="flex items-center gap-2">
          <VersionUploader binder={binder} onUploaded={reload} />
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="text-sm text-zinc-500 flex items-center gap-2">
            <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading versions…
          </div>
        ) : versions.length === 0 ? (
          <div className="text-sm text-zinc-500">No versions yet — upload one to get started.</div>
        ) : (
          <ul className="divide-y divide-zinc-800 text-sm">
            {versions.map((v) => (
              <li key={v.id} className="py-2 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-white">{v.version_label}</span>
                    <StatusPill status={v.status} />
                    <span className="text-[11px] text-zinc-500">{v.chunk_count} chunks · {v.fact_count} facts</span>
                  </div>
                  <div className="text-[11px] text-zinc-500 mt-0.5">
                    {v.source_filename || "unknown source"} · uploaded {new Date(v.created_at).toLocaleString()}
                  </div>
                </div>
                {v.status !== "active" && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => activate(v.id)}
                    className="border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10"
                    data-testid={`activate-version-${v.id}`}
                  >
                    <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Activate
                  </Button>
                )}
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

export default function LibraryView() {
  const { ctx } = useOutletContext();
  const [binders, setBinders] = useState([]);
  const [categories, setCategories] = useState([]);
  const [visibilities, setVisibilities] = useState(["sales_facing", "owner_vault"]);
  const [loading, setLoading] = useState(true);
  const [showVault, setShowVault] = useState(false);

  const isOwner = ctx?.role === "owner";

  const reload = async () => {
    setLoading(true);
    try {
      const [b, cats] = await Promise.all([
        crmApi.listBinders(showVault && isOwner),
        crmApi.categories(),
      ]);
      setBinders(b || []);
      setCategories(cats?.categories || []);
      setVisibilities(cats?.visibilities || ["sales_facing", "owner_vault"]);
    } catch (exc) {
      toast.error(exc.message || "Failed to load library");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      try {
        const [b, cats] = await Promise.all([
          crmApi.listBinders(showVault && isOwner),
          crmApi.categories(),
        ]);
        if (!active) return;
        setBinders(b || []);
        setCategories(cats?.categories || []);
        setVisibilities(cats?.visibilities || ["sales_facing", "owner_vault"]);
      } catch (exc) {
        if (active) toast.error(exc.message || "Failed to load library");
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [showVault, isOwner]);

  const grouped = useMemo(() => {
    const out = {};
    binders.forEach((b) => {
      out[b.category] = out[b.category] || [];
      out[b.category].push(b);
    });
    return out;
  }, [binders]);

  return (
    <div className="space-y-6" data-testid="owner-library">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Sales Knowledge Library</h1>
          <p className="text-sm text-zinc-400 mt-1">
            Every word the agents say about pricing, scope, or process comes from here.
            Upload a new version any time — agents pick it up on the next interaction. No rebuild required.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {isOwner && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowVault((v) => !v)}
              className="border-zinc-700 text-white"
              data-testid="toggle-vault"
            >
              <Lock className="h-3.5 w-3.5 mr-1" /> {showVault ? "Hide" : "Show"} Owner Vault binders
            </Button>
          )}
          <Button variant="ghost" size="sm" onClick={reload} className="text-zinc-300">
            <RefreshCw className="h-3.5 w-3.5 mr-1" /> Refresh
          </Button>
          <NewBinderDialog categories={categories} visibilities={visibilities} onCreated={reload} isOwner={isOwner} />
        </div>
      </div>

      {loading ? (
        <div className="text-sm text-zinc-400 flex items-center gap-2">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading binders…
        </div>
      ) : binders.length === 0 ? (
        <Card className="bg-zinc-900/40 border-zinc-800">
          <CardContent className="py-10 text-center text-sm text-zinc-400">
            <AlertCircle className="h-6 w-6 text-amber-300 mx-auto mb-2" />
            No binders yet. Create one to start uploading sales content — Subscriptions, Roofing, Remodeling, etc.
          </CardContent>
        </Card>
      ) : (
        Object.keys(grouped).sort().map((cat) => (
          <section key={cat} className="space-y-3">
            <h2 className="text-xs uppercase tracking-[0.18em] text-zinc-500">{cat}</h2>
            <div className="grid gap-3">
              {grouped[cat].map((b) => (
                <BinderRow key={b.id} binder={b} onChange={reload} />
              ))}
            </div>
          </section>
        ))
      )}
    </div>
  );
}
